import type { Control } from "../core/Control.js";
import type { VisualState } from "../themes/Theme.js";
import {
  compose,
  evaluateGroup,
  groupDuration,
  localTime,
  motionPreset,
  restValues,
  type AnimationGroupSpec,
  type MotionValues,
  type PresetOptions,
} from "./animation.js";

type Status = "idle" | "waiting" | "playing" | "done";

interface GroupState {
  spec: AnimationGroupSpec;
  name: string;
  total: number;
  status: Status;
  startedAt: number;
  /** Held triggers only: how far forward the group is, 0..1. */
  phase: number;
}

const HELD = new Set(["hover", "press", "focus"]);

/**
 * The animation groups on one control, and their clock.
 *
 * The counterpart of `FlareList`, and built the same way: created on first use, keeps the
 * control registered as a desktop animator for exactly as long as something is moving, and asks
 * nothing of the control it belongs to. Where a flare paints *around* a control, motion moves
 * the control itself - the parent applies the result as a translate, a scale and an opacity
 * around the child's whole paint, so a label rising in brings its text, its flare and anything
 * inside it along.
 *
 * Motion is drawing, not layout: the control's bounds, and so its hit testing, stay where the
 * layout put them. That is what lets an entrance run inside a Grid without the Grid noticing, and
 * it is why a hover lift of three pixels never makes the pointer fall off the thing it lifted.
 */
export class MotionList {
  #owner: Control;
  #groups: GroupState[] = [];
  #animating = false;
  #lastNow = -1;
  #values: MotionValues = restValues();

  constructor(owner: Control) {
    this.#owner = owner;
  }

  get length(): number {
    return this.#groups.length;
  }

  /** The groups, as they were added. */
  get specs(): AnimationGroupSpec[] {
    return this.#groups.map((g) => g.spec);
  }

  names(): string[] {
    return this.#groups.map((g) => g.name);
  }

  /** What the last frame came to. `reveal` is the one a control reads while painting. */
  get values(): Readonly<MotionValues> {
    return this.#values;
  }

  /** Add a group, or a preset by name. Returns the name it can be played and stopped by. */
  add(group: AnimationGroupSpec): string;
  add(preset: string, options?: PresetOptions): string;
  add(group: AnimationGroupSpec | string, options?: PresetOptions): string {
    const spec = typeof group === "string" ? motionPreset(group, options) : group;
    const name = this.#uniqueName(spec.name ?? spec.trigger);
    this.#groups.push({
      spec,
      name,
      total: groupDuration(spec),
      status: initialStatus(spec),
      startedAt: 0,
      phase: 0,
    });
    this.#owner.invalidate();
    return name;
  }

  remove(name: string): void {
    const i = this.#groups.findIndex((g) => g.name === name);
    if (i < 0) return;
    this.#groups.splice(i, 1);
    this.#owner.invalidate();
  }

  clear(): void {
    this.#groups.length = 0;
    this.#values = restValues();
    this.#sync(false);
    this.#owner.invalidate();
  }

  /**
   * Start a group from the beginning - every group when no name is given. A held group cannot be
   * played; it follows its state.
   */
  play(name?: string): void {
    for (const g of this.#matching(name)) {
      if (HELD.has(g.spec.trigger)) continue;
      g.status = "playing";
      g.startedAt = this.#now();
      this.#raise("animationplay", g.name);
    }
    this.#sync(true);
    this.#owner.invalidate();
  }

  /** Stop a group and take it off the control, as though it had never played. */
  stop(name?: string): void {
    for (const g of this.#matching(name)) {
      if (HELD.has(g.spec.trigger)) g.phase = 0;
      else g.status = "idle";
    }
    this.#owner.invalidate();
  }

  /**
   * Put every `load` and `reveal` group back to waiting, so each plays again the next time its
   * trigger holds. For a designer's "replay" button, and for a screen shown a second time that
   * should arrive the way it did the first.
   */
  replay(): void {
    for (const g of this.#groups) {
      if (g.spec.trigger === "load" || g.spec.trigger === "reveal") g.status = "waiting";
    }
    this.#owner.invalidate();
  }

  /**
   * Advance every group to `now` and fold them into one set of values.
   *
   * `onScreen` is whether the control's resting place intersects the visible area, which is the
   * whole of what `reveal` waits for. `reduced` is the user's request for less movement: an
   * entrance lands at once, a loop does not run, a held reaction jumps rather than glides.
   *
   * @internal Called by the parent while painting.
   */
  frame(now: number, onScreen: boolean, state: VisualState, reduced: boolean): MotionValues {
    const dt = this.#lastNow < 0 ? 0 : Math.max(0, now - this.#lastNow);
    this.#lastNow = now;
    const values = restValues();
    let active = false;

    for (const g of this.#groups) {
      const trigger = g.spec.trigger;
      const loops = (g.spec.looping ?? "none") !== "none";

      if (HELD.has(trigger)) {
        const target = heldBy(trigger, state) ? 1 : 0;
        if (reduced || g.total <= 0) g.phase = target;
        else {
          const step = dt / g.total;
          g.phase = target > g.phase ? Math.min(target, g.phase + step) : Math.max(target, g.phase - step);
        }
        if (g.phase !== target) active = true;
        if (g.phase > 0) compose(values, evaluateGroup(g.spec, g.phase * g.total));
        continue;
      }

      if (g.status === "waiting" && (trigger === "load" || (trigger === "reveal" && onScreen))) {
        g.status = "playing";
        g.startedAt = now;
        this.#raise("animationplay", g.name);
      }

      switch (g.status) {
        case "idle":
          break;
        case "waiting":
          compose(values, evaluateGroup(g.spec, 0));
          break;
        case "done":
          compose(values, evaluateGroup(g.spec, g.total));
          break;
        case "playing": {
          if (reduced && loops) break;
          const elapsed = reduced ? g.total : now - g.startedAt;
          if (!loops && elapsed >= g.total) {
            g.status = "done";
            compose(values, evaluateGroup(g.spec, g.total));
            this.#raise("animationfinished", g.name);
            break;
          }
          active = true;
          compose(values, evaluateGroup(g.spec, localTime(g.spec, elapsed)));
          break;
        }
      }
    }

    this.#values = values;
    this.#sync(active);
    return values;
  }

  /** Re-register with whichever desktop the control now belongs to. */
  refresh(): void {
    const was = this.#animating;
    this.#animating = false;
    this.#sync(was);
  }

  #sync(want: boolean): void {
    const desktop = this.#owner.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this.#owner);
    else desktop.removeAnimator(this.#owner);
  }

  #now(): number {
    return this.#owner.desktop?.frameTime ?? 0;
  }

  #matching(name: string | undefined): GroupState[] {
    return name === undefined ? this.#groups : this.#groups.filter((g) => g.name === name);
  }

  #uniqueName(base: string): string {
    let name = base;
    for (let n = 2; this.#groups.some((g) => g.name === name); n++) name = `${base}${n}`;
    return name;
  }

  /**
   * Raised after the paint that noticed it, never during: a handler that shows, hides or adds a
   * control would otherwise be editing the tree its own parent is halfway through painting.
   */
  #raise(event: "animationplay" | "animationfinished", name: string): void {
    const owner = this.#owner;
    queueMicrotask(() => owner.emit(event, name));
  }
}

function initialStatus(spec: AnimationGroupSpec): Status {
  return spec.trigger === "load" || spec.trigger === "reveal" ? "waiting" : "idle";
}

function heldBy(trigger: string, state: VisualState): boolean {
  if (trigger === "hover") return state.hovered && !state.disabled;
  if (trigger === "press") return state.pressed && !state.disabled;
  return state.focused;
}
